export const USER_EVENT_ORIGINS = ['cli', 'web', 'server', 'unknown'] as const;

export type UserEventOrigin = (typeof USER_EVENT_ORIGINS)[number];

export const USER_EVENT_TYPES = [
  'workflow.created',
  'workflow.create-failed',
  'workflow.approved',
  'workflow.promoted',
  'workflow.promote-failed',
  'workflow.maintained',
  'workflow.paused',
  'workflow.unpaused',
  'workflow.management-status-changed',
  'workflow.node-deactivated',
  'workflow.node-reactivated',
  'workflow.archived',
  'workflow.unarchived',
  'workflow.node-added',
  'workflow.node-add-failed',
  'workflow.run-started',
  'workflow.run.completed',
  'workflow.run.failed',
  'workflow.test-run-started',
  'workflow.test-failed',
  'test-suite.started',
  'test-suite.completed',
  'exploratory-test.created',
  'user.signed-up',
  'user.invited',
  'property.created',
  'api-key.created',
  'import.csv-completed',
  'import.csv-failed',
  'import.documents-completed',
  'command.started',
  'command.completed',
  'command.failed',
  'command.instrumentation-failed',
  'app-skill.groomed',
  'application-secret.set',
  'application-secret.removed',
  'sandbox-exec.requested',
  'sandbox-exec.completed',
  'sandbox-sync.session',
  'sandbox-sync.completed',
  'ade-template.published',
  'ade-template.build-failed',
] as const;

export type UserEventType = (typeof USER_EVENT_TYPES)[number];

/** Audit rows only the server writes; the client event routes refuse them. */
export const SERVER_ONLY_USER_EVENT_TYPES = [
  'application-secret.set',
  'application-secret.removed',
  'sandbox-exec.requested',
  'sandbox-exec.completed',
  'sandbox-sync.session',
  'sandbox-sync.completed',
  'ade-template.published',
  'ade-template.build-failed',
] as const;

/** An audit type only the server writes. */
export type ServerOnlyUserEventType = (typeof SERVER_ONLY_USER_EVENT_TYPES)[number];

export type CliCommandBaseMetadata = {
  commandPath: string;
  topLevelCommand: string;
  subcommand?: string;
  cliVersion: string;
  apiHost?: string;
  isInteractive: boolean;
  args?: string[];
  workflowDebugSubcommand?: string;
  workflowDebugTarget?: string;
  workflowDebugTargetSource?: string;
};

export type CliCommandStartedMetadata = CliCommandBaseMetadata;

export type CliCommandCompletedMetadata = CliCommandBaseMetadata & {
  durationMs: number;
  exitCode: number;
};

export type CliCommandFailedMetadata = CliCommandBaseMetadata & {
  durationMs: number;
  exitCode: number;
  errorName?: string;
  errorMessage?: string;
  errorKind?: string;
  reportToSentry?: boolean;
  sentryFingerprint?: string[];
  cliExternalRunOperation?: string;
  cliExternalRunId?: string | null;
  cliExternalRunPhase?: string;
  cliExternalRunFormat?: string;
  cliDaemonOperation?: string | null;
  cliDaemonRequestId?: string | null;
  cliDaemonMethod?: string | null;
  cliDaemonPath?: string | null;
  cliDaemonPhase?: string | null;
  cliDaemonAttempt?: number | null;
  cliDaemonMaxRetries?: number | null;
  cliDaemonPort?: number | null;
  cliDaemonElapsedMs?: number | null;
  cliDaemonTimeoutMs?: number | null;
  cliDaemonStatus?: number | null;
  cliDaemonPreviousAttempt?: number | null;
  cliDaemonEntrypoint?: string | null;
  cliDaemonEntrypointCandidates?: string | null;
  cliDaemonExecPath?: string | null;
  cliDaemonPlatform?: string | null;
  cliDaemonNodeVersion?: string | null;
  cliDaemonSpawnPid?: number | null;
  cliDaemonStdoutTail?: string | null;
  cliDaemonStdoutBytes?: number | null;
  cliDaemonExitCode?: number | null;
  cliDaemonExitSignal?: string | null;
  cliSessionId?: string | null;
  cliSessionTargetSource?: string | null;
  cliSessionRequestedTarget?: string | null;
  cliErrorCauseName?: string | null;
  cliErrorCauseMessage?: string | null;
  cliErrorCauseCode?: string | null;
  cliTerminationKind?: string | null;
  cliTerminationExpected?: boolean | null;
};

export type CliCommandInstrumentationFailedMetadata = CliCommandBaseMetadata & {
  stage: 'recorder-init' | 'record-started' | 'record-completed' | 'record-failed';
  errorName?: string;
  errorMessage?: string;
  errorKind?: string;
  reportToSentry?: boolean;
  sentryFingerprint?: string[];
};

export type UserEventPayloads = {
  'workflow.created': { workflowId: string; name?: string };
  'workflow.create-failed': { name?: string; error: string };
  'workflow.approved': { workflowId: string; name?: string };
  'workflow.promoted': { workflowId: string; name?: string };
  'workflow.promote-failed': { workflowId: string; name?: string; error: string };
  /**
   * The maintenance agent changed a promoted flow's definition (node-config
   * edits landed on the origin). Cache-only recaches do not record this.
   */
  'workflow.maintained': {
    workflowId: string;
    name: string;
    convergenceRunId: string;
    /** Agent-authored, customer-facing 1–2 sentences (`report_complete.changeSummary`). */
    changeSummary: string;
  };
  'workflow.paused': { workflowId: string; name?: string };
  'workflow.unpaused': { workflowId: string; name?: string };
  'workflow.management-status-changed': {
    workflowId: string;
    name?: string;
    fromStatus: string;
    toStatus: string;
    reason?: string;
    ticketCount?: number;
  };
  'workflow.node-deactivated': {
    workflowId: string;
    nodeId: string;
    nodeLabel?: string;
    reason?: string;
  };
  'workflow.node-reactivated': {
    workflowId: string;
    nodeId: string;
    nodeLabel?: string;
    reason?: string;
  };
  'workflow.archived': {
    workflowId: string;
    name?: string;
    flowType?: string;
    archivedFrom?: string;
    hadDependencies?: boolean;
    confirmationMethod?: string;
    timeToArchiveMs?: number;
  };
  'workflow.unarchived': {
    workflowId: string;
    name?: string;
    flowType?: string;
    /** ISO timestamp captured before the row was un-archived. */
    priorArchivedAt?: string;
    /** User id captured before the row was un-archived. */
    priorArchivedBy?: string;
    unarchivedFrom?: string;
  };
  'workflow.node-added': { workflowId: string; nodeType: string };
  'workflow.node-add-failed': { workflowId: string; error: string };
  'workflow.run-started': { workflowId: string; name?: string };
  'workflow.run.completed': {
    workflowId: string;
    runId: string;
    durationMs?: number;
    /** Flow name, included for designer-run "test passed" activity rendering. */
    name?: string;
  };
  'workflow.run.failed': { workflowId: string; runId: string; error?: string };
  'workflow.test-run-started': { workflowId: string; name?: string };
  'workflow.test-failed': {
    workflowId: string;
    name?: string;
    runId?: string;
    suiteExecutionId: string;
    error: string;
    failureType?: string;
    propertyName?: string;
    attempts?: number;
    testRunUrl: string;
  };
  'test-suite.started': {
    source: 'ci-cd' | 'web-ui' | 'web-ui-bulk';
    /** @deprecated single-tag legacy field — see `tags` */
    tagFilter?: string;
    tags?: string[];
    namePattern?: string;
    workflowCount?: number;
    propertyId?: string;
    environmentId?: string;
    /** Operator opted to clear `degraded` health on the in-scope flows and run them. */
    reEnableDegraded?: boolean;
  };
  /**
   * A suite run reached a terminal state. Attributed to `triggeredByUserId`
   * (a human) or to no actor (a schedule / automation, rendered as Cofactor).
   */
  'test-suite.completed': {
    executionId: string;
    /** The execution's terminal status — `completed_with_errors` also covers an orchestrator crash, where the totals alone can look green. */
    status: 'completed' | 'completed_with_errors' | 'completed_with_inconclusive';
    passed: number;
    failed: number;
    total: number;
    /** `'manual'` or `automation:<rule name>` — mirrors workflow_test_executions.triggered_reason. */
    triggeredReason: string;
  };
  'exploratory-test.created': { exploratoryTestId: string; name?: string };
  'user.signed-up': { email: string; method?: string };
  'user.invited': { email: string; invitedBy?: string };
  'property.created': { propertyId: string; name?: string; url?: string };
  'api-key.created': { keyPrefix: string };
  'import.csv-completed': {
    importId: string;
    sourceFilename: string;
    succeededRows: number;
    failedRows: number;
  };
  'import.csv-failed': { importId: string; sourceFilename: string; error: string };
  'import.documents-completed': {
    importId: string;
    sourceFilename: string;
    succeededRows: number;
    failedRows: number;
  };
  'command.started': CliCommandStartedMetadata;
  'command.completed': CliCommandCompletedMetadata;
  'command.failed': CliCommandFailedMetadata;
  'command.instrumentation-failed': CliCommandInstrumentationFailedMetadata;
  /** A scheduled app-skill grooming pass consolidated one section of a property. */
  'app-skill.groomed': {
    propertyId: string;
    propertyName?: string;
    section: string;
    /** Item count before → after consolidation. */
    before: number;
    after: number;
  };
  /**
   * Audit trail for a Sandboxes v2 application secret. Who is `actor_user_id`.
   * Never carries the value, a hash or a length. `environment` null = every
   * environment.
   */
  'application-secret.set': ApplicationSecretAuditMetadata;
  'application-secret.removed': ApplicationSecretAuditMetadata;
  /**
   * An ADE template version published (`actor_user_id` is the publisher), with
   * sha256 hashes of what every claimant will run: the yaml, setup prompt and
   * guidance. Never their text.
   */
  'ade-template.published': {
    templateId: string;
    name: string;
    version: number;
    sourceRepo: string;
    sourceCommit: string;
    yamlSha256: string;
    setupPromptSha256: string;
    guidanceSha256: string;
  };
  /** An ADE template version's build failed (written by the job; no actor). `error` is the tail of the failure, cut. */
  'ade-template.build-failed': { templateId: string; name: string; version: number; error: string };
  /**
   * Audit trail for a Sandboxes v2 `exec`. Who asked is `actor_user_id`.
   * `command` is the redacted copy stored on the run (injected secrets and
   * persona passwords removed; best effort: encoded values or credentials
   * typed inline are not caught), never the raw command. `apiKeyId` is the key
   * that made the call, null for a session: key attribution lives here, joined
   * to the rest by `runId`. A `requested` without a `completed` means the
   * command is still running, the job crashed, or it was never queued (so a
   * `requested` alone does not prove the command ran).
   */
  'sandbox-exec.requested': {
    sandboxId: string;
    runId: string;
    command: string;
    timeoutSeconds: number;
    apiKeyId: string | null;
  };
  /**
   * How an `exec` ended. `actor_user_id` is the requester. Never carries
   * output or error text. `exitCode` is null when the run failed before or
   * outside the command (old helper, helper failure).
   */
  'sandbox-exec.completed': {
    sandboxId: string;
    runId: string;
    status: 'succeeded' | 'failed';
    exitCode: number | null;
    timedOut: boolean;
  };
  /**
   * A sync session minted for a claimed copy (`POST .../sync-session`): its
   * token writes files into the copy until `expiresAt`. Who asked is
   * `actor_user_id`; `apiKeyId` is the key that made the call, null for a
   * session.
   */
  'sandbox-sync.session': {
    sandboxId: string;
    /** Whose claim the token writes into (any org member with `sandbox:exec` may sync it). */
    claimedByUserId: string | null;
    expiresAt: string;
    apiKeyId: string | null;
  };
  /**
   * A sync the CLI reports it applied (`POST .../syncs`): the synced tree id,
   * whether it was build output (`--to`), and how many files it wrote and
   * deleted. Reported by the CLI, not proven by the copy.
   */
  'sandbox-sync.completed': {
    sandboxId: string;
    tree: string;
    buildOutput: boolean;
    written: number;
    deleted: number;
    ms: number;
    apiKeyId: string | null;
  };
};

type ApplicationSecretAuditMetadata = {
  applicationId: string;
  environment: string | null;
  name: string;
};
